import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "@/hooks/use-toast";
import { Plus, Trash2, Loader2, ShieldAlert, Search, Check, X, Pencil, CheckSquare, Layers, Settings2, RotateCcw, ChevronDown, ChevronRight } from "lucide-react";
import { useEditableCategories } from "@/hooks/useEditableCategories";

interface EditingState {
  id: string;
  name: string;
  main_category: string;
  sub_category: string;
  finish: string;
}

export default function AdminComponents() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [editing, setEditing] = useState<EditingState | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [batchCategory, setBatchCategory] = useState("");
  const [batchSubCategory, setBatchSubCategory] = useState("");
  const [batchFinish, setBatchFinish] = useState("");
  const [categoryEditorOpen, setCategoryEditorOpen] = useState(false);
  const [newSubName, setNewSubName] = useState<Record<string, string>>({});

  const {
    categories: editableCategories,
    updateCategory,
    updateSubCategory,
    addSubCategory,
    removeSubCategory,
    resetToDefaults,
    getCategoryName,
  } = useEditableCategories();

  useEffect(() => {
    const check = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) { setIsAdmin(false); return; }
      const { data } = await supabase.rpc("has_role", { _user_id: session.user.id, _role: "admin" });
      setIsAdmin(data === true);
    };
    check();
  }, []);

  const { data: components, isLoading } = useQuery({
    queryKey: ["admin-components"],
    enabled: isAdmin === true,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("components")
        .select("*")
        .order("main_category", { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (edit: EditingState) => {
      const { error } = await supabase
        .from("components")
        .update({
          name: edit.name,
          main_category: edit.main_category,
          sub_category: edit.sub_category,
          finish: edit.finish || null,
        })
        .eq("id", edit.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-components"] });
      toast({ title: "Saved" });
      setEditing(null);
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const batchUpdateMutation = useMutation({
    mutationFn: async (payload: { ids: string[]; updates: Record<string, string | null> }) => {
      const promises = payload.ids.map((id) =>
        supabase.from("components").update(payload.updates).eq("id", id)
      );
      const results = await Promise.all(promises);
      const failed = results.filter((r) => r.error);
      if (failed.length > 0) throw new Error(`${failed.length} updates failed`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-components"] });
      toast({ title: "Batch updated", description: `${selected.size} components updated.` });
      clearBatch();
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const batchDeleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      const { error } = await supabase.from("components").delete().in("id", ids);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-components"] });
      toast({ title: "Deleted", description: `${selected.size} components deleted.` });
      setSelected(new Set());
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("components").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-components"] });
      toast({ title: "Deleted" });
    },
    onError: (err: any) => {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    },
  });

  const clearBatch = () => {
    setSelected(new Set());
    setBatchCategory("");
    setBatchSubCategory("");
    setBatchFinish("");
  };

  const toggleSelect = useCallback((id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);


  if (isAdmin === null) {
    return (
      <Layout>
        <div className="flex min-h-screen items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </Layout>
    );
  }

  if (isAdmin === false) {
    return (
      <Layout>
        <div className="flex min-h-screen flex-col items-center justify-center px-4">
          <Card className="max-w-md text-center">
            <CardContent className="pt-6">
              <ShieldAlert className="mx-auto mb-4 h-8 w-8 text-destructive" />
              <h1 className="font-serif text-2xl font-semibold">Access Denied</h1>
              <p className="mt-2 text-muted-foreground">Admin access required.</p>
              <Button onClick={() => navigate("/")} className="mt-4 w-full">Go Home</Button>
            </CardContent>
          </Card>
        </div>
      </Layout>
    );
  }

  // getCategoryName comes from useEditableCategories hook

  const filtered = (components || []).filter((c) => {
    const matchesSearch = !search || c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.sub_category.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = categoryFilter === "all" || c.main_category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const startEdit = (comp: typeof filtered[0]) => {
    setEditing({
      id: comp.id,
      name: comp.name,
      main_category: comp.main_category,
      sub_category: comp.sub_category,
      finish: comp.finish || "",
    });
  };

  const cancelEdit = () => setEditing(null);

  const saveEdit = () => {
    if (editing && editing.name && editing.main_category && editing.sub_category) {
      updateMutation.mutate(editing);
    }
  };

  const handleBatchApply = () => {
    const updates: Record<string, string | null> = {};
    if (batchCategory) updates.main_category = batchCategory;
    if (batchSubCategory) updates.sub_category = batchSubCategory;
    if (batchFinish) updates.finish = batchFinish;

    if (Object.keys(updates).length === 0) {
      toast({ title: "No changes", description: "Set at least one field to apply." });
      return;
    }

    batchUpdateMutation.mutate({ ids: Array.from(selected), updates });
  };

  const handleBatchDelete = () => {
    if (confirm(`Delete ${selected.size} component(s)? This cannot be undone.`)) {
      batchDeleteMutation.mutate(Array.from(selected));
    }
  };


  return (
    <Layout>
      <div className="min-h-screen bg-muted">
        <div className="mx-auto max-w-[1400px] px-6 pt-24 pb-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-serif text-2xl text-foreground">Components</h1>
              <p className="text-sm text-muted-foreground mt-1">{components?.length || 0} total</p>
            </div>
            <div className="flex gap-2">
              <Button asChild variant="outline" className="gap-2 font-sans text-xs uppercase tracking-wider">
                <Link to="/admin/components/new"><Plus size={14} /> New</Link>
              </Button>
            </div>
          </div>

          {/* Category Editor */}
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              className="gap-2 text-xs text-muted-foreground mb-3"
              onClick={() => setCategoryEditorOpen(!categoryEditorOpen)}
            >
              <Settings2 size={14} />
              Manage Categories
              {categoryEditorOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            </Button>

            {categoryEditorOpen && (
              <div className="rounded-lg border border-border bg-background p-5 space-y-5">
                <div className="flex items-center justify-between">
                  <p className="text-xs text-muted-foreground">Rename categories and subcategories, or add/remove subcategories.</p>
                  <Button variant="ghost" size="sm" className="gap-1 text-xs text-muted-foreground" onClick={resetToDefaults}>
                    <RotateCcw size={12} /> Reset
                  </Button>
                </div>

                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {editableCategories.map((cat) => (
                    <div key={cat.id} className="rounded-lg border border-border p-4 space-y-3">
                      {/* Category name */}
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Category</label>
                        <Input
                          value={cat.name}
                          onChange={(e) => updateCategory(cat.id, e.target.value)}
                          className="h-8 text-sm font-medium mt-1"
                        />
                      </div>

                      {/* Subcategories */}
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Subcategories</label>
                        <div className="mt-1 space-y-1.5">
                          {cat.subCategories.map((sub) => (
                            <div key={sub.id} className="flex items-center gap-1.5">
                              <Input
                                value={sub.name}
                                onChange={(e) => updateSubCategory(cat.id, sub.id, e.target.value)}
                                className="h-7 text-xs flex-1"
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 shrink-0 text-muted-foreground hover:text-destructive"
                                onClick={() => removeSubCategory(cat.id, sub.id)}
                              >
                                <X size={12} />
                              </Button>
                            </div>
                          ))}
                        </div>
                        {/* Add new subcategory */}
                        <div className="flex items-center gap-1.5 mt-2">
                          <Input
                            value={newSubName[cat.id] || ""}
                            onChange={(e) => setNewSubName({ ...newSubName, [cat.id]: e.target.value })}
                            placeholder="New subcategory…"
                            className="h-7 text-xs flex-1"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && newSubName[cat.id]?.trim()) {
                                addSubCategory(cat.id, newSubName[cat.id].trim());
                                setNewSubName({ ...newSubName, [cat.id]: "" });
                              }
                            }}
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 shrink-0 text-primary"
                            onClick={() => {
                              if (newSubName[cat.id]?.trim()) {
                                addSubCategory(cat.id, newSubName[cat.id].trim());
                                setNewSubName({ ...newSubName, [cat.id]: "" });
                              }
                            }}
                          >
                            <Plus size={12} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="relative mb-6 max-w-md">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search components..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 bg-background"
            />
          </div>

          {/* Category Filter Bar */}
          <div className="mb-6 flex flex-wrap items-center gap-2">
            <Button
              variant={categoryFilter === "all" ? "secondary" : "ghost"}
              size="sm"
              className="h-8 text-xs font-sans"
              onClick={() => setCategoryFilter("all")}
            >
              All ({components?.length || 0})
            </Button>
            {editableCategories.map((cat) => {
              const count = (components || []).filter((c) => c.main_category === cat.id).length;
              if (count === 0) return null;
              return (
                <Button
                  key={cat.id}
                  variant={categoryFilter === cat.id ? "secondary" : "ghost"}
                  size="sm"
                  className="h-8 text-xs font-sans"
                  onClick={() => setCategoryFilter(cat.id)}
                >
                  {cat.name} ({count})
                </Button>
              );
            })}
          </div>

          {/* Batch Action Bar */}
          {selected.size > 0 && (
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary/20 bg-primary/5 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                <CheckSquare size={16} className="text-primary" />
                <span>{selected.size} selected</span>
              </div>

              <div className="h-5 w-px bg-border" />

              {/* Batch Category */}
              <Select value={batchCategory} onValueChange={setBatchCategory}>
                <SelectTrigger className="h-8 w-[180px] text-xs bg-background">
                  <SelectValue placeholder="Set category…" />
                </SelectTrigger>
                <SelectContent>
                  {editableCategories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Batch Subcategory */}
              <Input
                value={batchSubCategory}
                onChange={(e) => setBatchSubCategory(e.target.value)}
                placeholder="Set subcategory…"
                className="h-8 w-[160px] text-xs bg-background"
              />

              {/* Batch Finish */}
              <Input
                value={batchFinish}
                onChange={(e) => setBatchFinish(e.target.value)}
                placeholder="Set finish…"
                className="h-8 w-[140px] text-xs bg-background"
              />

              <Button
                size="sm"
                className="h-8 gap-1 text-xs"
                onClick={handleBatchApply}
                disabled={batchUpdateMutation.isPending}
              >
                <Layers size={12} />
                {batchUpdateMutation.isPending ? "Applying…" : "Apply"}
              </Button>

              <div className="h-5 w-px bg-border" />

              <Button
                size="sm"
                variant="outline"
                className="h-8 gap-1 text-xs text-destructive hover:bg-destructive/10"
                onClick={handleBatchDelete}
                disabled={batchDeleteMutation.isPending}
              >
                <Trash2 size={12} />
                Delete
              </Button>

              <Button
                size="sm"
                variant="ghost"
                className="h-8 text-xs text-muted-foreground"
                onClick={clearBatch}
              >
                Clear
              </Button>
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-24 text-center">
              <h3 className="font-serif text-xl text-foreground mb-2">No components yet</h3>
              <p className="text-muted-foreground text-sm">Create your first component to get started.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {(() => {
                const grouped: Record<string, typeof filtered> = {};
                filtered.forEach((comp) => {
                  if (!grouped[comp.main_category]) grouped[comp.main_category] = [];
                  grouped[comp.main_category].push(comp);
                });

                const sortedKeys = Object.keys(grouped).sort((a, b) =>
                  getCategoryName(a).localeCompare(getCategoryName(b))
                );

                return sortedKeys.map((catKey) => (
                  <div key={catKey}>
                    <div className="mb-3 flex items-center gap-3">
                      <h2 className="font-serif text-lg font-medium text-foreground">
                        {getCategoryName(catKey)}
                      </h2>
                      <span className="text-xs text-muted-foreground">
                        {grouped[catKey].length} item{grouped[catKey].length !== 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                      {grouped[catKey].map((comp) => {
                        const isEditingRow = editing?.id === comp.id;
                        const isSelected = selected.has(comp.id);

                        return (
                          <div
                            key={comp.id}
                            className={`group relative overflow-hidden rounded-lg border bg-background transition-all duration-200 ${
                              isSelected ? "border-primary ring-1 ring-primary/20" : "border-border hover:border-muted-foreground/30"
                            }`}
                          >
                            {/* Image + select overlay */}
                            <div className="relative aspect-[4/3] overflow-hidden bg-muted">
                              {comp.image_url ? (
                                <img src={comp.image_url} alt={comp.name} className="h-full w-full object-cover" />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-muted-foreground/40">
                                  <Layers size={28} />
                                </div>
                              )}
                              {/* Top-left checkbox */}
                              <div className="absolute left-2 top-2">
                                <Checkbox
                                  checked={isSelected}
                                  onCheckedChange={() => toggleSelect(comp.id)}
                                  className="h-5 w-5 border-background/60 bg-background/60 backdrop-blur-sm data-[state=checked]:bg-primary data-[state=checked]:border-primary"
                                />
                              </div>
                              {/* Top-right actions */}
                              <div className="absolute right-2 top-2 flex gap-1 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                                <Button
                                  variant="secondary"
                                  size="icon"
                                  className="h-7 w-7 bg-background/80 backdrop-blur-sm hover:bg-background"
                                  onClick={() => navigate(`/admin/components/${comp.id}/edit`)}
                                >
                                  <Pencil size={12} />
                                </Button>
                                <Button
                                  variant="secondary"
                                  size="icon"
                                  className="h-7 w-7 bg-background/80 backdrop-blur-sm text-destructive hover:bg-destructive hover:text-destructive-foreground"
                                  onClick={() => { if (confirm("Delete this component?")) deleteMutation.mutate(comp.id); }}
                                >
                                  <Trash2 size={12} />
                                </Button>
                              </div>
                            </div>

                            {/* Content — inline edit or display */}
                            <div className="p-3 space-y-1.5">
                              {isEditingRow ? (
                                <div className="space-y-2">
                                  <Input
                                    value={editing.name}
                                    onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                                    className="h-7 text-xs"
                                    placeholder="Name"
                                  />
                                  <div className="grid grid-cols-2 gap-2">
                                    <Input
                                      value={editing.sub_category}
                                      onChange={(e) => setEditing({ ...editing, sub_category: e.target.value })}
                                      className="h-7 text-xs"
                                      placeholder="Subcategory"
                                    />
                                    <Input
                                      value={editing.finish}
                                      onChange={(e) => setEditing({ ...editing, finish: e.target.value })}
                                      className="h-7 text-xs"
                                      placeholder="Finish"
                                    />
                                  </div>
                                  <div className="flex justify-end gap-1 pt-1">
                                    <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-muted-foreground" onClick={cancelEdit}>
                                      Cancel
                                    </Button>
                                    <Button size="sm" className="h-6 px-3 text-xs" onClick={saveEdit} disabled={updateMutation.isPending}>
                                      {updateMutation.isPending ? "…" : "Save"}
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  className="cursor-pointer"
                                  onDoubleClick={() => startEdit(comp)}
                                  title="Double-click to edit"
                                >
                                  <p className="text-[10px] uppercase tracking-[0.12em] text-muted-foreground">
                                    {comp.sub_category}
                                    {comp.finish && <span className="ml-1 text-muted-foreground/50">· {comp.finish}</span>}
                                  </p>
                                  <h3 className="line-clamp-1 text-sm font-medium text-foreground">
                                    {comp.name}
                                  </h3>
                                  {comp.colors && comp.colors.length > 0 && (
                                    <div className="mt-1.5 flex -space-x-0.5">
                                      {comp.colors.slice(0, 5).map((color, i) => (
                                        <div
                                          key={i}
                                          className="h-3 w-3 rounded-full border border-background"
                                          style={{ backgroundColor: color }}
                                        />
                                      ))}
                                      {comp.colors.length > 5 && (
                                        <span className="ml-1 text-[9px] text-muted-foreground">+{comp.colors.length - 5}</span>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
